import { inject } from 'vue'
import { AxiosInstance } from 'axios'
import dayjs from './dayjs.js'
import { useStore } from '@/js/stores/index.js';

// Helpers composable
export function useHelpers()
{
	// We use composable here, because axios can be different based on the environment, for example this funciton can be used in both "user" and "admin"
	const axios = inject("axios") as AxiosInstance
	if (!axios) throw new Error('Axios not provided');

	// Preload resources
	function preloadResource(urls: string[])
	{
		const calls: Promise<any>[] = []

		urls.forEach(url => {
			calls.push(axios.get(url))
		})

		return Promise.all(calls)
	}

	return { preloadResource }
}

export function preloadImages(images: string[])
{
	return new Promise<void>(async resolve => {
		if (!images.length) {
			resolve(); // Nothing to do, immediately resolve
			return;
		}

		const imagePromises = images.map((src) => {
			return new Promise<void>(res => {
				const image = new Image()
				image.src = src;

				image.onload = () => {
					res(); // Resolve when the image is successfully loaded
				};

				image.onerror = () => {
					res(); // Also resolve on error to allow other images to load
				};
			});
		});

		// Wait for all images to either load or error
		await Promise.all(imagePromises)

		// Resolve once all images have either loaded or errored
		resolve(); 
	})
}

export function isHidden(el: Element): boolean
{
	var style = window.getComputedStyle(el);
	return (style.display === 'none')
}

/**
 * Get YouTube movie ID from the url
 * @param url 
 * @returns id or `null` if not found
 */
function youtubeParser(url: string): string|null
{
	const match = url.match(/.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|live\/|watch\?v=)([^#&?]*).*/);
	return (match && match[7].length==11) ? match[7] : null
}

function makeYoutubeIframe(url: string): string
{
	return '<iframe width="420" height="315" src="https://www.youtube.com/embed/'+youtubeParser(url)+'" referrerPolicy="strict-origin-when-cross-origin"></iframe>'
}

// Convert all <oembed> tags to youtube iframes
export function convertOembedToResponsiveYoutubeIframes()
{
	document.querySelectorAll('oembed').forEach((element: any) => {
		element.outerHTML = makeYoutubeIframeResponsive( makeYoutubeIframe(element.getAttribute("url")) )
	})
}

/**
 * Comvert YouTube Iframe HTML with static dimensions to a responsive one
 * @param html HTML input
 * @returns HTML string
 */
export function makeYoutubeIframeResponsive(html: string): string
{
	return html.replace(
		/<iframe.*?src="(.*?youtube\.com.*?)".*?<\/iframe>/mg,
		'<div style="position: relative; width: 100%; height: 0; padding-bottom: 56.25%;"><iframe src="$1?hl=hr&iv_load_policy=3&modestbranding=1&rel=0&showinfo=0" frameborder="0" allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>'
	);
}

export function getTranslation(translations: any, language: string|null = null): string
{
	const store = useStore()

	// No translations at all
	if(!translations || typeof translations !== 'object') return ""

	// If language var is not provided take the global one
	if(!language && typeof store.currentLanguage !== "undefined") language = store.currentLanguage

	// Check if we have all necessary data. If not fallback to any first language
	if(typeof language === "undefined" || !language || !translations[language]){
		for (var i = 0; i < Object.keys(translations).length; i++) {
			if(translations[Object.keys(translations)[i]]) return translations[Object.keys(translations)[i]]
		}

		return ""
	}

	// Return correct translation
	return translations[language]
}

export function getChallengeImageUrl(imageId: string|number): string
{
	// Check if imageId is numeric, if so - use it as system image id
	if(typeof imageId == "number" || parseInt(imageId)){
		return `/image/i.${imageId}/w.400:h.400:f.0/image.png`
	}else{
		return '/challenge-images/' + (imageId ? imageId : '_.png')
	}
}

/**
 * Replace texts in array with other texts in array within a string 
 * @param str 
 * @param findArray 
 * @param replaceArray 
 * @returns 
 */
export function replaceArray( str: string, findArray: string[], replaceArray: string[] ): string
{
	const regex = [], map: any = {}
	for(let i = 0; i < findArray.length; i++) {
		regex.push( findArray[i].replace(/([-[\]{}()*+?.\\^$|#,])/g,'\\$1') );
		map[findArray[i]] = replaceArray[i];
	}

	return str.replace( new RegExp(regex.join('|'), 'g'), matched => map[matched])
}

export function loadScriptAsync(url: string)
{
	return new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		script.src = url;
		script.async = true;

		script.onload = () => {
			resolve()
		}

		script.onerror = () => {
			reject(new Error(`Error loading script: ${url}`));
		};

		document.head.appendChild(script);
	});
}

/**
 * Validate and cleanup the email address within the string
 * @param email 
 * @returns 
 */
export function validateEmail(email: string): boolean
{
	const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
	return re.test(String(email).toLowerCase())
}

/**
 * Returns true if the string contains numbers only
 * 
 * @param str to check
 * 
 * @returns `true` if only numbers are in the string, `false` otherwise
 */
export function containsOnlyNumbers(str: string): boolean
{
  return /^\d+$/.test(str)
}

/**
 * Replace text tokens in the string of format "%1 %2" with numbered values insertText(str, arg1, arg2)
 *
 * @param str string to replace
 * @param args any number of tokens to be injected as %1, %2, etc into a string
 * @returns string with injected values
 */
export function insertText(str: string, ...args: string[]): string
{
	if(!str) return '';

	for(var i = 0; i < args.length; i++){
		str = str.replace("%" + (i+1), args[i])
	}

	return str
}

// Strip HTML tags from string
export function stripTags(str: string): string
{
	return str.replace(/(<([^>]+)>)/ig, "")
}

export function formatPrizeCost(cost: string): string
{
	return insertText(useStore().translations?.rewardsCostFormat || "", cost)
}

export function humanize(value: string): string
{
	return dayjs(value).fromNow()
		.replace(/(za|prije) dan/, '$1 1 dan') /* Fix for croatian language */
		.replace(/(za|prije) mjesec/, '$1 mjesec dana')
}

/**
 * Get value in seconds
 * @param val 
 * @returns 
 */
export function getTimerText(val: number): string
{
	if(isNaN(val)) return 'NaN'
	
	const days = Math.floor(val / 86400)
	if(days > 0){
		return dayjs().add(val, 's').fromNow(true) // Parse string if more than 1 day in future
	}

	const hours = Math.floor(val % 86400 / 3600)
	const minutes = Math.floor((val % 3600) / 60)
	const seconds = Math.floor(val % 60)

	if(hours){
		return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
	}else if(minutes){
		return `${minutes}:${String(seconds).padStart(2, '0')}`
	}else if(seconds){
		return `${seconds}s`
	}

	return "";
}

export function randomHash (nChar: number): string
{
	// convert number of characters to number of bytes
	const nBytes = Math.ceil(nChar = (+nChar || 32) / 2)

	// create a typed array of that many bytes
	const u = new Uint8Array(nBytes)

	// populate it wit crypto-random values
	window.crypto.getRandomValues(u)

	// convert it to an Array of Strings (e.g. "01", "AF", ..)
	const zpad = (str: string) => '00'.slice(str.length) + str

	const a = Array.prototype.map.call(u, (x) => zpad(x.toString(16)))

	// Array of String to String
	let str = a.join('')

	// and snip off the excess digit if we want an odd number
	if (nChar % 2) str = str.slice(1)

	// return what we made
	return str
}

/**
 * Generate random key safe to share (it means without 0/O and l/1 similiarities)
 * @param length of the string to generate
 * @returns 
 */
export function generateKey(length: number = 32): string
{
	// These are safe symbols for reading with 1/l and 0/O removed
	const safeSymbols = 'abcdefghijkmnpqrstuvwxyzABCDEFGHIJKMNPQRSTUVWXYZ23456789';
	const safeSymbolsLength = safeSymbols.length
	let string = ""

	for(let i = 0; i < length; i++) string += safeSymbols[getRandomInt(safeSymbolsLength - 1)]

	return string
}

/**
 * Replace "\n" to a newline in all textarea placeholders on the html page
 */
export function fixTextareaPlaceholderNewlines(): void
{
	// Fixing textarea placeholder
	var textAreas = document.getElementsByTagName('textarea')

	Array.prototype.forEach.call(textAreas, elem => {
		elem.placeholder = elem.placeholder.replace(/\\n/g, '\n')
	})
}

/**
 * Check if the variable is a real object (not an array, null or regex)
 * @param obj 
 * @returns 
 */
export function isRealObject(obj: any): boolean
{
	return (obj ?? false)?.constructor?.name === "Object"
}

/**
 * Check object for any non-empty (truethy) keys
 *
 * @param Object Object to check
 * @returns boolean true if there is any non-empty key in the object
 */
export function hasNonEmptyKey(obj: Record<string, any>): boolean
{
	// Check if the input is an object
	if (typeof obj !== 'object' || obj === null) {
		throw new Error('Input must be a non-null object');
	}

	// Iterate over the keys of the object
	for (const key in obj) {
		// Check if the key is a property of the object
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			// Check if the value is non-empty (truthy)
			if (obj[key]) {
				return true; // Found a non-empty key
			}
		}
	}

	return false; // No non-empty keys found
}

/**
 * This is a helper to wait for the condition to happen, it should be wrapped in an async function:
 * async function a(){
 *  await until(() => value == 5);
 *	...
 * 	doSomethingWhenValueBecomes5()
 * }
 * @param {*} conditionFunction
 * @returns Promise
 */
export function until(conditionFunction: any)
{
  const poll = (resolve: Function) => {
    if(conditionFunction()) resolve();
    else setTimeout(() => poll(resolve), 400)
  }

  return new Promise(poll);
}

/**
 * Promise friendly setTimeout() wrapper
 *
 * @param Function Code to execute
 * @param Integer delay in milliseconds
 * @returns Promise
 */
export function setTimeoutPromise(cb: Function, delay: number){
	return new Promise(function(resolve) {
		setTimeout(() => {
			resolve(cb())
		}, delay);
	});
}

/**
 * Set all values of the provided object to false
 *
 * @param obj an object to set all keys to false
 * @returns
 */
export function setAllValuesFalse(obj: any[]): void
{
	Object.keys(obj).forEach((i: any) => obj[i] = false)
}

/**
 * Download a string as a file
 * @param {string} data The string content to download
 * @param {string} fileName The name of the file
 * @param {string} [mimeType="text/plain"] (optional) The MIME type of the file if `data` is provided as String
 */
export function downloadStringAsFile(data: string|Blob, fileName: string, mimeType: string = "text/plain"): void
{
	// Create a temporary <a> element
	const a = document.createElement('a');

	let url = "";
	if (data instanceof Blob) {
		// If data is a Blob, create object URL
		url = URL.createObjectURL(data);
		a.setAttribute('href', url);
	} else {
		// If data is a string, use data URL
		a.setAttribute('href', 'data:' + mimeType + ';charset=utf-8,' + encodeURIComponent(data));
	}

	a.download = fileName;  // Set the desired file name

	// Append the <a> to the body (not visible to the user)
	document.body.appendChild(a);

	// Programmatically trigger the click event
	a.click();

	// Clean up
	document.body.removeChild(a);

	// Revoke object URL if we created one for Blob
	if (data instanceof Blob) {
		URL.revokeObjectURL(url);
	}
}

/**
 * Scroll browser window into an lement
 *
 * @param {Element} element
 */
export function scrollTo(element: Element): void
{
	element?.scrollIntoView({
		behavior: "smooth",
		block: "nearest"
	})
}

/**
 * Generate random integer from 0 to `max`
 * @param max (default: 1)
 * @returns 
 */
export function getRandomInt(max: number = 1): number
{
  return Math.floor(Math.random() * (max + 1) );
}